// Telex BP-1002 — per-panel help data.
//
// Hotspots resolve against `[data-hotspot-id]` attributes on the SVG
// controls; the HotspotLayer measures live element rects at runtime.
//
// HARD RULES applied:
//   • #1 source authority — facts traced to docs/sources/equipment/
//     telex-bp-1002-2002-manual.pdf and CO-160 (no BTV / CVS / branch).
//   • #2 no fabricated citations — copy paraphrases the manual.
//   • #8 no internal sources — welcome card is plain prose.

import type { PanelHelpData } from "../../help/help-types";

export const TELEX_BP_1002_HELP: PanelHelpData = {
  welcome: {
    title: "Telex BP-1002 Beltpack",
    eyebrow: "Intercom · single-channel Audiocom beltpack",
    body:
      "This is the intercom beltpack you'll wear during the program. It connects you to the rest of the AV crew over a single Audiocom channel — Technical Director, audio operators, stage. The volume knob on the left sets your headset listen level. The Call button (red) signals the channel that you need attention without having to talk; the Talk button (green) opens your microphone so others can hear you. In default latch mode, a single tap toggles Talk on or off; in momentary mode you press and hold to talk. Most crew operate in latch mode for hands-free use during cues.",
    highlights: [
      "Volume knob — your headset listen level only; doesn't affect anyone else.",
      "Call button (red) — silent attention signal; press to flash the channel's call indicator.",
      "Talk button (green) — opens your mic onto the channel.",
      "Latch vs Momentary — tap toggles vs press-and-hold; default is latch.",
      "Always check Talk is OFF before discussing anything not for the whole channel.",
    ],
  },
  hotspots: [
    {
      id: "volume-knob",
      targetSelector: "[data-hotspot-id='volume-knob']",
      label: "Volume knob",
      body:
        "Sets the listen volume in your own headset. Has no effect on what anyone else hears, and no effect on whether you're transmitting. Drag the knob to rotate it, scroll the wheel over it, or use the arrow keys when it's focused.",
    },
    {
      id: "call-btn",
      targetSelector: "[data-hotspot-id='call-btn']",
      label: "Call (red)",
      body:
        "Press to send a silent call signal to the channel — every other beltpack on the channel briefly lights its red Call LED. Use this to get someone's attention when you don't want to interrupt audio (for example, during a speaker's prayer or a quiet program moment). Keyboard: C.",
    },
    {
      id: "talk-btn",
      targetSelector: "[data-hotspot-id='talk-btn']",
      label: "Talk (green)",
      body:
        "Opens your microphone onto the channel. The green LED is lit while your mic is hot. In latch mode (default) one tap turns Talk on, the next tap turns it off. In momentary mode you must press and hold. Keyboard: T.",
    },
  ],
  shortcuts: [
    { keys: "T", action: "Talk (latch: tap toggles · momentary: press and hold)" },
    { keys: "C", action: "Call — momentary attention signal to the channel" },
    { keys: "↑ / ↓", action: "Headset volume up / down" },
  ],
};
